import ApplicationModel, { attr } from '../ApplicationModel';
import EstimateTypeFilter from '../filters/EstimateTypeFilter';
import type ItemFilter from '../filters/ItemFilter';
import RecordFilter from '../filters/RecordFilter';
import { type UnitType } from '../lib/Estimate';
import { nameMap } from '../lib/nameMap';
import { model } from '../modelBuilder';
import Epic from './Epic';
import Feature from './Feature';
import Release from './Release';
import ReleaseResourceEvent from './ReleaseResourceEvent';
import Requirement from './Requirement';
import User from './User';
import WorkflowStatus from './WorkflowStatus';
import { HasBookmarkFilters } from './concerns/HasBookmarkFilters';
import { type BookmarksReleaseRetrospectiveAttributes } from './generated/BookmarksReleaseRetrospectiveAttributes';

interface Local {
  availableReleases?: Release[];
  release?: Release;
  filterObjects: Record<string, ItemFilter<any>>;
}

interface BookmarksReleaseRetrospective
  extends BookmarksReleaseRetrospectiveAttributes,
    HasBookmarkFilters<{
      releases?: RecordFilter;
      estimation_type?: EstimateTypeFilter;
      iterations?: RecordFilter;
    }> {
  local: Local;
}

@model
@HasBookmarkFilters
class BookmarksReleaseRetrospective extends ApplicationModel {
  // Start auto-generated
  static typename: 'BookmarksReleaseRetrospective' =
    'BookmarksReleaseRetrospective';
  get typename(): 'BookmarksReleaseRetrospective' {
    return 'BookmarksReleaseRetrospective';
  }

  static internalTypename: 'Bookmarks::ReleaseRetrospective' =
    'Bookmarks::ReleaseRetrospective';
  get internalTypename(): 'Bookmarks::ReleaseRetrospective' {
    return 'Bookmarks::ReleaseRetrospective';
  }

  static bookmarkName = 'Bookmarks::ReleaseRetrospective';

  static inputType = 'ReleaseRetrospectiveAttributes!';

  static fields = {
    filters: attr({ writable: true, readable: true, json: true }),
    hideDevelopPromo: attr({ writable: true, readable: false }),
    id: attr({ readable: true }),
    projectId: attr({ readable: true }),
    project: attr.belongsTo({ readable: true, typename: 'Project' }),
    releaseDateHistory: attr.hasMany({ readable: true }),
  };
  // End auto-generated
  //
  initialize() {
    this.local = {
      filterObjects: {},
    };
    this.filters = this.filters || {};
  }

  async createFilters() {
    const releases = await this.availableReleases();

    let release: Release | undefined;
    if (this.filters.releases) {
      const releaseId = this.filters.releases[0];
      release = releases.find(i => i.id === releaseId);
    }

    if (!release && releases.length > 0) {
      // Default to most recently completed release
      release =
        releases.find(r => r.workflowStatus?.internalMeaning === 'SHIPPED') ||
        releases[0];
    }

    const defaultEstimationType = 'count';

    const estimateTypeFilter = new EstimateTypeFilter({
      initialItems:
        this.filters.estimation_type?.length > 0
          ? this.filters.estimation_type
          : [defaultEstimationType],
      capacityUnits: release?.capacityUnits,
      showRemainingEstimate: true,
    });

    const releaseFilter = new RecordFilter(
      this.filters.releases?.length > 0 ? this.filters.releases : [release?.id],
      releases.map(nameMap)
    );

    return {
      releases: releaseFilter,
      estimation_type: estimateTypeFilter,
    };
  }

  async availableReleases() {
    if (this.local.availableReleases) return this.local.availableReleases;

    if (!this.project) return [];

    const releases = await Release.select(
      'id',
      'name',
      'capacityUnits',
      'developmentStartedOn',
      'releasedOn'
    )
      .merge({
        workflowStatus: WorkflowStatus.select(
          'internalMeaning',
          'name',
          'color'
        ),
      })
      .where({
        projectId: this.project.id,
        hasDevelopmentStartedOn: true,
        parkingLot: false,
      })
      .order({ developmentStartedOn: 'DESC' })
      .per(100)
      .all();

    releases.materializeRecords();
    this.local.availableReleases = releases;

    return releases;
  }

  units(): UnitType {
    const units =
      this.getFilter('estimation_type')?.single() || 'originalEstimate';

    if (units === 'count') {
      return 'COUNT';
    } else {
      return this.local.release?.capacityUnits || 'POINTS';
    }
  }

  async getReleaseEvents(releaseId: RecordId): Promise<ReleaseResourceEvent[]> {
    const scope = ReleaseResourceEvent.select(
      'id',
      'kind',
      'occurredOn',
      'resourceUnitChange',
      'itemCountChange'
    )
      .merge({
        resourceable: Feature.select(
          'id',
          'name',
          'teamId',
          'path',
          'showFeatureRemainingEstimate'
        )
          .merge({
            assignedToUser: User.select('id'),
            remainingEstimate: ['value'],
            originalEstimate: ['value'],
            initialEstimate: ['value'],
          })
          .union(
            Requirement.select('id', 'name', 'teamId', 'path').merge({
              assignedToUser: User.select('id'),
              remainingEstimate: ['value'],
              originalEstimate: ['value'],
              initialEstimate: ['value'],
            })
          )
          .union(
            Epic.select(
              'id',
              'name',
              'teamId',
              'path',
              'showFeatureRemainingEstimate'
            ).merge({
              assignedToUser: User.select('id'),
              remainingEstimate: ['value'],
              originalEstimate: ['value'],
              initialEstimate: ['value'],
            })
          ),
      })
      .where({ releaseId: releaseId })
      .per(500);

    return scope.findInBatches({ concurrency: 2 });
  }
}

export default BookmarksReleaseRetrospective;
